import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { TopProduto } from '../types'
import {
  Users,
  Smartphone,
  Wrench,
  ShoppingCart,
  TrendingUp,
  Clock,
  AlertTriangle,
  Euro,
  Calendar,
  Plus,
  Package,
  BarChart3
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts'
import { useAuth } from '../context/AuthContext'
import relatoriosService, {
  formatarValor,
  formatarNumero,
  coresGrafico
} from '../services/relatorios'
import dashboardService, { 
  formatCurrency, 
  formatNumber, 
  chartColors,
  osStatusColors,
  osStatusLabels,
  pagamentoLabels 
} from '../services/dashboard'
import Button from '../components/ui/Button'

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  // Queries para dados do dashboard usando o novo service
  // CORREÇÃO #003: Cache melhorado - dados sempre frescos ao recarregar
  const { data: dashboardData, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['dashboard', 'data'], // Removido date - permite refetch manual
    queryFn: async () => {
      const data = await relatoriosService.getDashboardData('30')
      console.log('🔍 DEBUG DASHBOARD DATA RECEBIDOS:', data)
      console.log('   - Receita Total:', data?.resumo?.receita?.total)
      console.log('   - Vendas Total:', data?.resumo?.vendas?.total)
      return data
    },
    staleTime: 0, // Sempre considerar stale - força refetch
    refetchOnMount: 'always', // Sempre refetch ao abrir dashboard
    refetchInterval: 1000 * 60 * 2 // Refetch a cada 2 minutos (reduzido de 5)
  })

  // Manter queries antigas como fallback
  const { data: metrics, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['dashboard', 'metrics'], // Removido date
    queryFn: async () => {
      const data = await dashboardService.getMetrics()
      console.log('🔍 DEBUG METRICS RECEBIDOS:', data)
      console.log('   - Faturamento Hoje:', data?.faturamento?.hoje)
      console.log('   - Faturamento Mês:', data?.faturamento?.mes)
      console.log('   - Faturamento Ano:', data?.faturamento?.ano)
      return data
    },
    staleTime: 0, // Sempre stale
    refetchOnMount: 'always', // Sempre refetch
    refetchInterval: 1000 * 60 * 2 // Refetch a cada 2 minutos
  })

  const { data: vendasChart, isLoading: isLoadingVendasChart } = useQuery({
    queryKey: ['dashboard', 'vendas-chart-debug-v4'], // Static key to avoid loops
    queryFn: () => dashboardService.getVendasChart(),
    staleTime: 0, // Force fresh fetch
    refetchOnWindowFocus: false, // Prevent refetch on focus
    cacheTime: 0 // Don't cache at all
  })

  const { data: topProdutos, isLoading: isLoadingTopProdutos } = useQuery({
    queryKey: ['dashboard', 'top-produtos'],
    queryFn: () => dashboardService.getTopProdutos(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  })

  const { data: osStatus, isLoading: isLoadingOSStatus } = useQuery({
    queryKey: ['dashboard', 'os-status'],
    queryFn: () => dashboardService.getOSStatus(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: 1000 * 60 * 2 // Refetch a cada 2 minutos
  })

  const { data: vendasPagamento, isLoading: isLoadingVendasPagamento } = useQuery({
    queryKey: ['dashboard', 'vendas-pagamento'],
    queryFn: () => dashboardService.getVendasPagamento(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  })

  const { data: atividadeRecente, isLoading: isLoadingAtividade } = useQuery({
    queryKey: ['dashboard', 'atividade-recente'],
    queryFn: () => dashboardService.getAtividadeRecente(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  })

  const { data: alertas, isLoading: isLoadingAlertas } = useQuery({
    queryKey: ['dashboard', 'alerts'],
    queryFn: () => dashboardService.getAlertas(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  })

  // Buscar relatório de vendas do mês para ter ticket médio correto
  const { data: relatorioVendasMes } = useQuery({
    queryKey: ['dashboard', 'relatorio-vendas-mes'],
    queryFn: async () => {
      const dataFim = new Date().toISOString().split('T')[0]
      const dataInicio = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      return relatoriosService.getRelatorioVendas({ dataInicio, dataFim })
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  })

  const isLoading = isLoadingMetrics || isLoadingDashboard

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Bom dia'
    if (hour < 18) return 'Boa tarde'
    return 'Boa noite'
  }

  if (isLoading) {
    return (
      <div className="p-8 space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const statCards = [
    {
      title: 'Faturamento Mensal',
      value: formatarValor(metrics?.faturamento.mes || 0),
      subtitle: `${formatarValor(metrics?.faturamento.hoje || 0)} hoje`,
      icon: Euro,
      color: 'purple',
      trend: dashboardData?.resumo.receita.crescimento
    },
    {
      title: 'Vendas do Mês',
      value: formatarNumero(metrics?.vendas.mes || 0),
      subtitle: `${metrics?.vendas.hoje || 0} hoje`,
      icon: ShoppingCart,
      color: 'emerald',
      trend: dashboardData?.resumo.vendas.crescimento
    },
    {
      title: 'Ordens de Serviço',
      value: formatarNumero(dashboardData?.resumo.ordensServico.total || metrics?.ordensServico.total || 0),
      subtitle: `${metrics?.ordensServico.emAndamento || 0} em andamento`,
      icon: Wrench,
      color: 'blue',
      trend: dashboardData?.resumo.ordensServico.crescimento
    },
    {
      title: 'Clientes Ativos',
      value: formatarNumero(metrics?.clientes.ativos || 0),
      subtitle: `+${metrics?.clientes.novosHoje || 0} novos`,
      icon: Users,
      color: 'green'
    },
    {
      title: 'Produtos Ativos',
      value: formatarNumero(metrics?.produtos.ativos || 0),
      subtitle: `${metrics?.produtos.estoqueZero || 0} sem stock`,
      icon: Package,
      color: 'indigo'
    },
    {
      title: 'Ticket Médio',
      value: formatarValor(relatorioVendasMes?.resumo.ticketMedio || 0),
      subtitle: 'Por venda (só vendas)',
      icon: TrendingUp,
      color: 'cyan'
    }
  ]

  const quickActions = [
    {
      title: 'Nova Ordem de Serviço',
      description: 'Criar uma nova ordem de serviço',
      icon: Wrench,
      color: 'blue',
      href: '/ordens-servico'
    },
    {
      title: 'Novo Cliente',
      description: 'Cadastrar um novo cliente',
      icon: Users,
      color: 'green',
      href: '/clientes'
    },
    {
      title: 'Nova Venda',
      description: 'Registrar uma nova venda',
      icon: ShoppingCart,
      color: 'purple',
      href: '/vendas'
    },
    {
      title: 'Verificar Stock',
      description: 'Consultar produtos em stock',
      icon: AlertTriangle,
      color: 'orange',
      href: '/estoque'
    }
  ]

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {getGreeting()}, {user?.nome}!
          </h1>
          <p className="text-gray-600 mt-1">
            Aqui está o resumo da sua oficina hoje.
          </p>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Calendar className="h-4 w-4" />
          <span>{new Date().toLocaleDateString('pt-PT', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-gray-500">{card.subtitle}</p>
                  {card.trend !== undefined && (
                    <div className={`flex items-center gap-1 text-xs ${
                      card.trend >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <TrendingUp className={`h-3 w-3 ${card.trend < 0 ? 'rotate-180' : ''}`} />
                      <span>{Math.abs(card.trend).toFixed(1)}%</span>
                    </div>
                  )}
                </div>
              </div>
              <div className={`p-3 rounded-lg bg-${card.color}-100`}>
                <card.icon className={`h-6 w-6 text-${card.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickActions.map((action, index) => (
          <div 
            key={index} 
            onClick={() => navigate(action.href)}
            className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
          >
            <div className="flex items-start space-x-4">
              <div className={`p-2 rounded-lg bg-${action.color}-100 group-hover:bg-${action.color}-200 transition-colors`}>
                <action.icon className={`h-5 w-5 text-${action.color}-600`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                  {action.title}
                </h3>
                <p className="text-xs text-gray-500 mt-1">{action.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Gráfico de Vendas */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">Vendas dos Últimos 30 Dias</h2>
            </div>
          </div>
          <div className="p-6">
            {isLoadingVendasChart ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={vendasChart || []}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="data"
                    stroke="#666"
                    fontSize={12}
                    tick={{ fill: '#666' }}
                  />
                  <YAxis
                    yAxisId="vendas"
                    orientation="left"
                    stroke="#8884d8"
                    fontSize={12}
                    tick={{ fill: '#8884d8' }}
                    label={{ value: 'Vendas', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  />
                  <YAxis
                    yAxisId="receita"
                    orientation="right"
                    stroke="#82ca9d"
                    fontSize={12}
                    tick={{ fill: '#82ca9d' }}
                    tickFormatter={(value) => {
                      if (value >= 1000) {
                        return `€${(value / 1000).toFixed(1)}k`
                      } else {
                        return `€${value}`
                      }
                    }}
                    label={{ value: 'Receita (€)', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value: any, name: string) => {
                      if (name === 'Receita') {
                        return [formatarValor(Number(value)), 'Receita']
                      }
                      return [formatarNumero(Number(value)), 'Vendas']
                    }}
                    labelFormatter={(label) => `Data: ${label}`}
                  />
                  
                  <Line
                    yAxisId="vendas"
                    type="monotone"
                    dataKey="vendas"
                    stroke="#8884d8"
                    strokeWidth={3}
                    name="Vendas"
                    dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#8884d8', strokeWidth: 2, fill: '#fff' }}
                    connectNulls={false}
                  />
                  <Line
                    yAxisId="receita"
                    type="monotone"
                    dataKey="faturamento"
                    stroke="#82ca9d"
                    strokeWidth={3}
                    name="Receita"
                    dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#82ca9d', strokeWidth: 2, fill: '#fff' }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Estados das OS */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-purple-500" />
                <h2 className="text-lg font-semibold text-gray-900">Estados das Ordens de Serviço</h2>
              </div>
              {osStatus && osStatus.length > 0 && (
                <div className="text-sm text-gray-500">
                  Total: {osStatus[0]?.total || osStatus.reduce((sum, item) => sum + item.quantidade, 0)} OS
                </div>
              )}
            </div>
          </div>
          <div className="p-6">
            {isLoadingOSStatus ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : osStatus && osStatus.length > 0 ? (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={osStatus.map(item => ({
                        name: item.nome,
                        value: item.quantidade,
                        fill: item.cor
                      }))}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={45}
                      dataKey="value"
                      label={false}
                      labelLine={false}
                      
                    >
                      {osStatus.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.cor}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value} OS`, name]}
                      labelFormatter={(label) => `Estado: ${label}`}
                    />
                    <Legend
                      layout="horizontal"
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{
                        paddingTop: "15px",
                        fontSize: "11px",
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: "5px"
                      }}
                      iconSize={8}
                      iconType="circle"
                    />
                    
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-gray-500">
                <BarChart3 className="h-12 w-12 mb-4" />
                <h3 className="text-lg font-medium">Nenhuma ordem de serviço encontrada</h3>
                <p className="text-sm">Quando houver ordens de serviço, a distribuição por estados aparecerá aqui.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Produtos e Formas de Pagamento */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Produtos */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Package className="h-5 w-5 text-green-500" />
              <h2 className="text-lg font-semibold text-gray-900">Top Produtos (30 dias)</h2>
            </div>
          </div>
          <div className="p-6">
            {isLoadingTopProdutos ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {topProdutos?.slice(0, 5).map((produto, index) => {
                  const nome = produto.produto?.nome || 'Produto'
                  const sku = produto.produto?.codigoInterno || 'N/A'
                  const quantidade = produto.quantidadeVendida || 0
                  const valor = produto.faturamento || 0
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-green-600">{index + 1}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{nome}</p>
                          <p className="text-xs text-gray-500">SKU: {sku}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">
                          {formatarNumero(quantidade)} un
                        </p>
                        <p className="text-xs text-green-600">
                          {formatarValor(valor)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Formas de Pagamento */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Euro className="h-5 w-5 text-indigo-500" />
              <h2 className="text-lg font-semibold text-gray-900">Formas de Pagamento (30 dias)</h2>
            </div>
          </div>
          <div className="p-6">
            {isLoadingVendasPagamento ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={vendasPagamento?.map((item, index) => {
                      const coresPagamento = {
                        'DINHEIRO': '#22c55e',      // Verde
                        'MBWAY': '#3b82f6',         // Azul
                        'MULTIBANCO': '#f59e0b',    // Laranja
                        'TRANSFERENCIA': '#8b5cf6'  // Roxo
                      }
                      return {
                        name: pagamentoLabels[item.formaPagamento] || item.formaPagamento,
                        value: item.valor,
                        quantidade: item.quantidade,
                        fill: coresPagamento[item.formaPagamento] || chartColors.primary
                      }
                    })}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={40}
                    dataKey="value"
                    label={false}
                    labelLine={false}
                    
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div style={{
                            backgroundColor: 'white',
                            padding: '12px 16px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}>
                            <p style={{ fontWeight: 600, marginBottom: '4px', color: data.fill }}>{data.name}</p>
                            <p style={{ fontSize: '14px', margin: '2px 0' }}>Valor: {formatCurrency(data.value)}</p>
                            <p style={{ fontSize: '12px', color: '#6b7280' }}>{data.quantidade} transações</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: '10px',
                      fontSize: '11px',
                      lineHeight: '1.5'
                    }}
                    iconSize={8}
                    iconType="circle"
                    formatter={(value, entry) => {
                      const { payload } = entry;
                      const formattedValue = new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(payload.value);
                      return `${value}: ${formattedValue}`;
                    }}
                  />
                  </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Alertas e Atividade Recente */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Alertas */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-gray-900">Alertas</h2>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {isLoadingAlertas ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-start space-x-3">
                    <div className="w-2 h-2 bg-gray-200 rounded-full mt-2"></div>
                    <div>
                      <div className="h-4 bg-gray-200 rounded w-40 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Produtos sem stock */}
                {alertas?.semEstoque && alertas.semEstoque.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <p className="text-sm font-medium text-gray-900">
                        {alertas.semEstoque.length} produtos sem stock
                      </p>
                    </div>
                    <div className="ml-4 space-y-1">
                      {alertas.semEstoque.map(produto => (
                        <p key={produto.id} className="text-xs text-gray-600">
                          • {produto.nome} ({produto.codigoInterno})
                        </p>
                      ))}
                    </div>
                    <p className="text-xs text-red-600 ml-4">Requer reposição imediata</p>
                  </div>
                )}

                {/* Produtos com stock mínimo */}
                {alertas?.estoqueMinimo && alertas.estoqueMinimo.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <p className="text-sm font-medium text-gray-900">
                        {alertas.estoqueMinimo.length} produtos com stock mínimo
                      </p>
                    </div>
                    <div className="ml-4 space-y-1">
                      {alertas.estoqueMinimo.map(produto => (
                        <p key={produto.id} className="text-xs text-gray-600">
                          • {produto.nome} ({produto.codigoInterno}) - {produto.stockAtual}/{produto.stockMinimo}
                        </p>
                      ))}
                    </div>
                    <p className="text-xs text-yellow-600 ml-4">Considere reposição</p>
                  </div>
                )}

                {/* Ordens de serviço abertas */}
                {metrics?.ordensServico.abertas && metrics.ordensServico.abertas > 0 && (
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {metrics.ordensServico.abertas} ordens aguardando diagnóstico
                      </p>
                      <p className="text-xs text-gray-500">Requer atenção da equipe técnica</p>
                    </div>
                  </div>
                )}

                {/* Mensagem quando não há alertas */}
                {(!alertas?.semEstoque?.length && !alertas?.estoqueMinimo?.length && !metrics?.ordensServico.abertas) && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">Nenhum alerta no momento</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Atividade Recente */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">Atividade Recente</h2>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {isLoadingAtividade ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div>
                      <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-48"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {atividadeRecente?.slice(0, 5).map((atividade, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      atividade.tipo === 'venda' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {atividade.tipo === 'venda' ? (
                        <ShoppingCart className="h-4 w-4 text-green-600" />
                      ) : (
                        <Wrench className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{atividade.titulo}</p>
                      <p className="text-xs text-gray-500">
                        {atividade.descricao} - {formatCurrency(atividade.valor)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage